import { Link } from "react-router-dom";
import { ArrowLeft, Target, Users, Zap, GraduationCap, Shield, MapPin } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <p className="uppercase tracking-widest text-white/60 text-sm font-bold mb-3">Our Story</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mb-4">
            Built by a Teacher.<br />For Every Student.
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            ClearPath wasn't born in a startup incubator. It was born in a classroom — from a teacher who got tired of watching students fail tests they were fully capable of passing.
          </p>
        </div>
      </header>

      {/* Founder Section */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <section className="mb-16">
          <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-8 md:p-12">
            {/* Founder Header */}
            <div className="flex flex-col sm:flex-row items-center gap-6 mb-8 pb-8 border-b border-slate-100">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-full flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-10 h-10 text-white" />
              </div>
              <div className="text-center sm:text-left">
                <h2 className="font-heading font-bold text-2xl text-slate-800">Rashaad Richardson</h2>
                <p className="text-slate-500 font-medium">Founder · Teacher · Veteran</p>
              </div>
            </div>

            {/* Quote */}
            <blockquote className="bg-indigo-50 border-l-4 border-indigo-500 rounded-r-2xl p-6 mb-8">
              <p className="text-slate-700 text-lg italic leading-relaxed">
                "I know what it feels like to sit in front of a standardized test and feel completely unprepared. I know the pressure, the anxiety, the self-doubt. I built ClearPath so no student ever has to feel that way again."
              </p>
            </blockquote>

            {/* Story */}
            <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
              <p>
                My name is Rashaad Richardson. I'm a first-year high school teacher in Texas and a United States Army veteran. But before any of that — I was a student who struggled.
              </p>
              <p>
                I grew up with dyslexia. Standardized tests were one of the hardest things I faced. It wasn't that I wasn't smart or that I didn't try — it was the pressure of sitting down for a high-stakes test and not feeling like I had truly prepared the right way. That feeling is something I never forgot.
              </p>
              <p>
                Now I stand in front of a classroom and I see the same look on my students' faces that I used to have. Smart, capable kids carrying the weight of a test that feels bigger than they are — and not having the right tools to feel ready for it.
              </p>
              <p>
                That's exactly why I built ClearPath. Not just to help students pass a test — but to help them walk in feeling prepared, confident, and ready. Because I know firsthand that the biggest difference between passing and failing is often not ability. <strong>It's preparation.</strong>
              </p>
              <p className="font-semibold text-slate-800">
                Every student deserves to feel prepared. That's not a luxury. That's what ClearPath is here to make possible for every single student — no matter where they come from.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center">
              <p className="font-heading font-black text-3xl text-indigo-600">1st</p>
              <p className="text-slate-500 text-sm font-medium mt-1">Year Teaching</p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center">
              <div className="flex items-center justify-center gap-1.5">
                <Shield className="w-5 h-5 text-green-600" />
                <p className="font-heading font-black text-3xl text-green-600">Army</p>
              </div>
              <p className="text-slate-500 text-sm font-medium mt-1">Veteran</p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center">
              <div className="flex items-center justify-center gap-1.5">
                <MapPin className="w-5 h-5 text-orange-500" />
                <p className="font-heading font-black text-3xl text-orange-500">TX</p>
              </div>
              <p className="text-slate-500 text-sm font-medium mt-1">Built for Texas</p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center">
              <p className="font-heading font-black text-3xl text-sky-600">5+</p>
              <p className="text-slate-500 text-sm font-medium mt-1">Tests Covered</p>
            </div>
          </div>
        </section>

        {/* What We Stand For */}
        <section className="mb-16">
          <h2 className="font-heading font-bold text-2xl text-slate-800 mb-8 text-center">What We Stand For</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-2">Built for Real Students</h3>
              <p className="text-slate-500 text-sm">Not a Silicon Valley product designed in a boardroom. ClearPath was built by a teacher who sees students struggle every single day — and refused to accept that as normal.</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center">
              <div className="w-14 h-14 bg-lime-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-lime-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-2">No Student Left Behind</h3>
              <p className="text-slate-500 text-sm">Every student deserves the same tools that wealthy families pay hundreds of dollars for. ClearPath makes that possible for every zip code in Texas.</p>
            </div>

            <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 text-center">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-2">Tools That Actually Work</h3>
              <p className="text-slate-500 text-sm">We built an AI tutor that explains the why behind every answer — not just tells you if you're right or wrong. Real understanding. Real results.</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-3xl p-8 md:p-12 text-white text-center">
            <h2 className="font-heading font-bold text-2xl mb-4">Ready to Get Started?</h2>
            <p className="text-white/90 text-lg mb-8 max-w-xl mx-auto">
              Practice, get help when stuck, and know when you're ready.
            </p>
            <Link
              to="/register"
              className="inline-block px-10 py-4 bg-lime-400 text-slate-800 font-bold rounded-xl hover:bg-lime-300 transition-colors text-lg"
              data-testid="about-cta-start-free"
            >
              Start Free Today
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 text-center text-slate-500 text-sm">
        © 2025 Clear Path Testing Solutions LLC. All rights reserved.
      </footer>
    </div>
  );
};

export default About;
